// Wizard de onboarding de novo cliente — 6 steps + revisão.
// Cada step é opcional (exceto Identidade) — usuário pode pular e finalizar.

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

type Step = "identidade" | "branding" | "redes" | "reportei" | "linhas" | "equipe" | "finalizar";

const STEPS: { id: Step; label: string; required: boolean }[] = [
  { id: "identidade", label: "Identidade",         required: true  },
  { id: "branding",   label: "Branding",           required: false },
  { id: "redes",      label: "Redes Sociais",      required: false },
  { id: "reportei",   label: "Reportei (métricas)", required: false },
  { id: "linhas",     label: "Linhas Editoriais",  required: false },
  { id: "equipe",     label: "Equipe Inicial",     required: false },
  { id: "finalizar",  label: "Concluir",           required: true  },
];

const REDES = [
  { key: "linkedin",  label: "LinkedIn",  color: "#0A66C2" },
  { key: "instagram", label: "Instagram", color: "#E1306C" },
  { key: "tiktok",    label: "TikTok",    color: "#010101" },
  { key: "youtube",   label: "YouTube",   color: "#FF0000" },
];

export function NewClientWizard({ open, onClose }: { open: boolean; onClose: (createdClientId?: number) => void }) {
  const [step, setStep] = useState<Step>("identidade");
  const [createdClientId, setCreatedClientId] = useState<number | null>(null);

  // Step 1: Identidade
  const [nome, setNome] = useState("");
  const [slug, setSlug] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [plano, setPlano] = useState<"starter" | "pro" | "enterprise">("pro");
  const [descricao, setDescricao] = useState("");
  const [contato, setContato] = useState("");

  // Step 2: Branding
  const [corPrimaria, setCorPrimaria] = useState("#c14727");

  // Step 3: Redes ativas + handle/URL por rede (config básica)
  const [redesAtivas, setRedesAtivas] = useState<Record<string, boolean>>({
    linkedin: true, instagram: true, tiktok: true, youtube: true,
  });
  const [redesCfg, setRedesCfg] = useState<Record<string, { handle: string; url: string }>>({
    linkedin:  { handle: "", url: "" },
    instagram: { handle: "", url: "" },
    tiktok:    { handle: "", url: "" },
    youtube:   { handle: "", url: "" },
  });

  // Step 4: Reportei
  const [reportei, setReportei] = useState({
    apiToken: "", projectId: "",
    igIntegrationId: "", liIntegrationId: "",
    igHandle: "", liHandle: "", ttHandle: "", ytHandle: "",
  });

  // Step 5: linhas editoriais — cliente novo agora começa SEM linhas (não
  // auto-seedamos mais). Esse step só explica como cadastrar depois pela UI.

  // Step 6: Equipe inicial
  const [adminEmail, setAdminEmail] = useState("");
  const [adminNome, setAdminNome] = useState("");

  // Mutations
  const utils = trpc.useUtils();
  const createMut = trpc.clients.create.useMutation();
  const updateMut = trpc.clients.update.useMutation();
  const setNetworkMut = trpc.clients.setNetwork.useMutation();
  const upsertNetworkCfgMut = trpc.clients.upsertNetworkConfig.useMutation();
  const upsertReporteiMut = trpc.clients.upsertReporteiConfig.useMutation();
  const inviteMut = trpc.clientAuth.invite.useMutation();

  function reset() {
    setStep("identidade");
    setCreatedClientId(null);
    setNome(""); setSlug(""); setSubdomain(""); setDescricao(""); setContato("");
    setPlano("pro"); setCorPrimaria("#c14727");
    setRedesAtivas({ linkedin: true, instagram: true, tiktok: true, youtube: true });
    setRedesCfg({ linkedin: { handle: "", url: "" }, instagram: { handle: "", url: "" }, tiktok: { handle: "", url: "" }, youtube: { handle: "", url: "" } });
    setReportei({ apiToken: "", projectId: "", igIntegrationId: "", liIntegrationId: "", igHandle: "", liHandle: "", ttHandle: "", ytHandle: "" });
    setAdminEmail(""); setAdminNome("");
  }

  function autoSlug(v: string) {
    return v.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  }

  async function handleCreateClient() {
    if (!nome || !slug || !subdomain) {
      toast.error("Nome, slug e subdomain são obrigatórios");
      return;
    }
    try {
      const res = await createMut.mutateAsync({
        nome, slug, subdomain,
        primaryColor: corPrimaria,
        descricao: descricao || undefined,
        contato: contato || undefined,
        plano,
      });
      setCreatedClientId(res.id);
      toast.success(`Cliente "${nome}" criado (id ${res.id}). Onboarding iniciado.`);
      setStep("branding");
    } catch (e: any) {
      toast.error(e?.message ?? "Falha ao criar cliente");
    }
  }

  async function handleSaveRedes() {
    if (!createdClientId) return;
    try {
      // Salva ativo + config (handle/url) por rede em paralelo
      await Promise.all(
        REDES.flatMap(r => {
          const ativo = redesAtivas[r.key];
          const cfg = redesCfg[r.key];
          const tasks: Promise<any>[] = [
            setNetworkMut.mutateAsync({ clientId: createdClientId, rede: r.key, ativo }),
          ];
          if (ativo && (cfg.handle || cfg.url)) {
            tasks.push(
              upsertNetworkCfgMut.mutateAsync({
                clientId: createdClientId,
                rede: r.key,
                handle: cfg.handle || null,
                url: cfg.url || null,
              })
            );
          }
          return tasks;
        })
      );
      toast.success("Redes salvas");
      setStep("reportei");
    } catch (e: any) { toast.error(e?.message ?? "Falha ao salvar redes"); }
  }

  async function handleSaveReportei() {
    if (!createdClientId) return;
    if (!reportei.apiToken && !reportei.projectId) {
      // Pulou
      setStep("linhas");
      return;
    }
    try {
      await upsertReporteiMut.mutateAsync({
        clientId: createdClientId,
        ...reportei,
      });
      toast.success("Reportei configurado");
      setStep("linhas");
    } catch (e: any) { toast.error(e?.message ?? "Falha ao salvar Reportei"); }
  }

  async function handleInviteAdmin() {
    if (!createdClientId) return;
    if (!adminEmail) { setStep("finalizar"); return; }
    try {
      await inviteMut.mutateAsync({
        email: adminEmail,
        name: adminNome || adminEmail.split("@")[0],
        clientId: createdClientId,
        role: "admin",
        origin: window.location.origin,
      });
      toast.success(`Convite enviado para ${adminEmail}`);
      setStep("finalizar");
    } catch (e: any) { toast.error(e?.message ?? "Falha ao enviar convite (sistema de email pode estar offline — pode convidar depois)"); }
  }

  function handleFinish() {
    utils.clients.list.invalidate();
    const id = createdClientId;
    reset();
    onClose(id ?? undefined);
  }

  if (!open) return null;

  const currentIdx = STEPS.findIndex(s => s.id === step);
  const isLast = step === "finalizar";

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm" onClick={() => onClose()} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col pointer-events-auto border border-border">

          {/* Header com stepper */}
          <div className="px-6 py-4 border-b border-border shrink-0">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-base text-foreground">Onboarding de novo cliente</h2>
              <button onClick={() => onClose()} className="text-muted-foreground hover:text-foreground">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              {STEPS.map((s, i) => (
                <div key={s.id} className="flex-1 flex items-center gap-1.5">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${
                        i < currentIdx ? "bg-green-500 text-white" :
                        i === currentIdx ? "bg-foreground text-background" :
                        "bg-muted text-muted-foreground"
                      }`}
                    >
                      {i < currentIdx ? "✓" : i + 1}
                    </div>
                    <span className={`text-[9px] mt-1 text-center ${i === currentIdx ? "text-foreground font-semibold" : "text-muted-foreground"}`}>
                      {s.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`h-0.5 flex-1 -mt-4 ${i < currentIdx ? "bg-green-500" : "bg-muted"}`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">

            {step === "identidade" && (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Vamos começar com os dados básicos do cliente. Os outros passos são opcionais — você pode preencher depois.</p>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">Nome do cliente *</label>
                  <Input value={nome} onChange={e => { setNome(e.target.value); if (!slug) setSlug(autoSlug(e.target.value)); }} placeholder="Ex: Acme Logística" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">Slug *</label>
                    <Input value={slug} onChange={e => setSlug(autoSlug(e.target.value))} placeholder="acme-logistica" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">Subdomain *</label>
                    <Input value={subdomain} onChange={e => setSubdomain(autoSlug(e.target.value))} placeholder="acme" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">Plano</label>
                    <Select value={plano} onValueChange={v => setPlano(v as any)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="starter">Starter</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">Email de contato</label>
                    <Input value={contato} onChange={e => setContato(e.target.value)} placeholder="contato@cliente.com.br" />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">Descrição (opcional)</label>
                  <Input value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="O que esse cliente faz..." />
                </div>
              </div>
            )}

            {step === "branding" && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Personalização visual. Você pode atualizar depois em <strong>Admin → {nome} → Branding</strong>.</p>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">Cor primária</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={corPrimaria} onChange={e => setCorPrimaria(e.target.value)} className="w-12 h-10 rounded border border-border cursor-pointer" />
                    <Input value={corPrimaria} onChange={e => setCorPrimaria(e.target.value)} className="font-mono" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground italic">Upload de logo será feito na tela do Admin após o onboarding (precisa de UI específica de imagem).</p>
              </div>
            )}

            {step === "redes" && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Quais redes este cliente vai usar? Pra cada rede ativa, você pode já cadastrar o @ e a URL pública (opcional — dá pra fazer depois em Admin → Integrações).
                </p>
                <div className="space-y-2">
                  {REDES.map(r => {
                    const ativo = !!redesAtivas[r.key];
                    return (
                      <div key={r.key} className="rounded-lg border border-border overflow-hidden">
                        <label className="flex items-center gap-3 p-3 cursor-pointer hover:bg-accent">
                          <input
                            type="checkbox"
                            checked={ativo}
                            onChange={e => setRedesAtivas(prev => ({ ...prev, [r.key]: e.target.checked }))}
                            className="w-4 h-4 accent-foreground"
                          />
                          <div className="w-3 h-3 rounded-full" style={{ background: r.color }} />
                          <span className="font-medium text-foreground flex-1">{r.label}</span>
                          {ativo && redesCfg[r.key].handle && (
                            <span className="text-xs text-muted-foreground">{redesCfg[r.key].handle}</span>
                          )}
                        </label>
                        {ativo && (
                          <div className="px-3 pb-3 pt-1 grid grid-cols-2 gap-2 bg-muted/30">
                            <Input
                              value={redesCfg[r.key].handle}
                              onChange={e => setRedesCfg(prev => ({ ...prev, [r.key]: { ...prev[r.key], handle: e.target.value } }))}
                              placeholder={r.key === "linkedin" ? "empresa" : "@cliente"}
                              className="text-xs h-8"
                            />
                            <Input
                              value={redesCfg[r.key].url}
                              onChange={e => setRedesCfg(prev => ({ ...prev, [r.key]: { ...prev[r.key], url: e.target.value } }))}
                              placeholder={`https://${r.key}.com/...`}
                              className="text-xs h-8"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {step === "reportei" && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Integração com Reportei pra puxar métricas de IG/LinkedIn automaticamente. Pode pular e configurar depois.</p>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">API Token</label>
                  <Input type="password" value={reportei.apiToken} onChange={e => setReportei(p => ({ ...p, apiToken: e.target.value }))} placeholder="reportei_xxxxxx" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">Project ID</label>
                  <Input value={reportei.projectId} onChange={e => setReportei(p => ({ ...p, projectId: e.target.value }))} placeholder="proj_xxxxxx" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">@ Instagram</label>
                    <Input value={reportei.igHandle} onChange={e => setReportei(p => ({ ...p, igHandle: e.target.value }))} placeholder="@cliente" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">IG Integration ID</label>
                    <Input value={reportei.igIntegrationId} onChange={e => setReportei(p => ({ ...p, igIntegrationId: e.target.value }))} placeholder="opcional" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">@ LinkedIn</label>
                    <Input value={reportei.liHandle} onChange={e => setReportei(p => ({ ...p, liHandle: e.target.value }))} placeholder="cliente-corp" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">LI Integration ID</label>
                    <Input value={reportei.liIntegrationId} onChange={e => setReportei(p => ({ ...p, liIntegrationId: e.target.value }))} placeholder="opcional" />
                  </div>
                </div>
              </div>
            )}

            {step === "linhas" && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Linhas editoriais são específicas de cada nicho — então <strong>{nome}</strong> começa sem linhas configuradas.
                </p>
                <div className="p-4 rounded-lg bg-accent border border-border space-y-3">
                  <p className="text-sm font-semibold text-foreground">Como cadastrar depois:</p>
                  <div className="text-xs text-muted-foreground space-y-2">
                    <p>1. Vá em <strong>Admin → Linhas Editoriais</strong></p>
                    <p>2. Selecione <strong>{nome}</strong> no dropdown</p>
                    <p>3. Use uma das opções:</p>
                    <ul className="ml-4 space-y-1 list-disc">
                      <li>Clicar <strong>+ Nova linha</strong> pra criar do zero (recomendado)</li>
                      <li>Usar <strong>Semear template do EmiteAí</strong> se o nicho do cliente for próximo (B2B compliance/SaaS) — depois é só editar nomes/cores</li>
                    </ul>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground italic">
                  Sugestão: defina 4–6 linhas com pilares e frequência % por linha. Aparecem como chips/filtros em Pautas, Peças, Calendário e Insights.
                </p>
              </div>
            )}

            {step === "equipe" && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Quer já convidar o admin do cliente? (Pode fazer depois também)</p>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">Nome</label>
                  <Input value={adminNome} onChange={e => setAdminNome(e.target.value)} placeholder="Ex: João Silva" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">Email</label>
                  <Input value={adminEmail} onChange={e => setAdminEmail(e.target.value)} placeholder="admin@cliente.com.br" />
                </div>
                <p className="text-xs text-muted-foreground italic">
                  ⚠ O envio do convite por email requer Resend ativo com domínio verificado.
                  Se falhar, você pode resetar a senha desse admin direto pelo banco.
                </p>
              </div>
            )}

            {step === "finalizar" && (
              <div className="space-y-4 text-center py-6">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-foreground">{nome} pronto pra rodar!</h3>
                  <p className="text-sm text-muted-foreground mt-1">Cliente criado com sucesso (id {createdClientId}).</p>
                </div>
                <div className="text-left bg-accent p-4 rounded-lg space-y-1 text-xs">
                  <p><strong>Slug:</strong> {slug}</p>
                  <p><strong>Subdomain:</strong> {subdomain}</p>
                  <p><strong>Plano:</strong> {plano}</p>
                  <p><strong>Redes ativas:</strong> {Object.entries(redesAtivas).filter(([_, v]) => v).map(([k]) => k).join(", ") || "nenhuma"}</p>
                  <p><strong>Reportei:</strong> {reportei.apiToken || reportei.projectId ? "configurado" : "pulado"}</p>
                  <p><strong>Linhas editoriais:</strong> 6 (template EmiteAí)</p>
                  <p><strong>Admin convidado:</strong> {adminEmail || "—"}</p>
                </div>
                <p className="text-xs text-muted-foreground italic">
                  Você pode editar tudo via <strong>Admin → {nome}</strong>.
                </p>
              </div>
            )}

          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border flex justify-between items-center shrink-0">
            <Button variant="outline" onClick={() => onClose()}>
              {step === "finalizar" ? "Fechar" : "Cancelar"}
            </Button>
            <div className="flex gap-2">
              {step !== "identidade" && step !== "finalizar" && (
                <Button variant="outline" onClick={() => {
                  const order: Step[] = ["identidade","branding","redes","reportei","linhas","equipe","finalizar"];
                  const idx = order.indexOf(step);
                  if (idx > 0) setStep(order[idx - 1]);
                }}>
                  ← Voltar
                </Button>
              )}
              {step === "identidade" && (
                <Button onClick={handleCreateClient} disabled={createMut.isPending}>
                  {createMut.isPending ? "Criando…" : "Criar cliente →"}
                </Button>
              )}
              {step === "branding" && (
                <>
                  <Button variant="outline" onClick={() => setStep("redes")}>Pular</Button>
                  <Button onClick={async () => {
                    if (createdClientId) await updateMut.mutateAsync({ id: createdClientId, primaryColor: corPrimaria }).catch(() => {});
                    setStep("redes");
                  }}>Salvar e continuar →</Button>
                </>
              )}
              {step === "redes" && (
                <Button onClick={handleSaveRedes} disabled={setNetworkMut.isPending}>Salvar e continuar →</Button>
              )}
              {step === "reportei" && (
                <>
                  <Button variant="outline" onClick={() => setStep("linhas")}>Pular</Button>
                  <Button onClick={handleSaveReportei} disabled={upsertReporteiMut.isPending}>Salvar e continuar →</Button>
                </>
              )}
              {step === "linhas" && (
                <Button onClick={() => setStep("equipe")}>Continuar →</Button>
              )}
              {step === "equipe" && (
                <>
                  <Button variant="outline" onClick={() => setStep("finalizar")}>Pular</Button>
                  <Button onClick={handleInviteAdmin} disabled={inviteMut.isPending}>
                    {adminEmail ? (inviteMut.isPending ? "Convidando…" : "Convidar e continuar →") : "Continuar →"}
                  </Button>
                </>
              )}
              {step === "finalizar" && (
                <Button onClick={handleFinish}>Concluir</Button>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
